/** Delay before a pending load may show a skeleton (avoids flash on fast responses). */
export const SKELETON_SHOW_DELAY_MS = 1000;

/** Minimum time a skeleton stays visible once shown (avoids strobe on near-threshold loads). */
export const SKELETON_MIN_VISIBLE_MS = 500;

export type DelayedLoadingListener = (showSkeleton: boolean) => void;

/** Options when clearing a pending load. */
export interface DelayedLoadingSettleOptions {
  /**
   * When true, hide a visible skeleton immediately (error/timeout path).
   * Success clears should omit this so the minimum hold is honored.
   */
  bypassMinHold?: boolean;
}

/**
 * Controls skeleton visibility with show-after delay and minimum hold once shown.
 * Used for Create Shipment initial page loads (OM-711 / OM-715).
 */
export class DelayedLoadingController {
  private pending = false;
  private visible = false;
  private shownAtMs: number | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(
    private readonly listener: DelayedLoadingListener,
    private readonly delayMs: number = SKELETON_SHOW_DELAY_MS,
    private readonly minVisibleMs: number = SKELETON_MIN_VISIBLE_MS,
    private readonly now: () => number = () => Date.now(),
  ) {}

  /**
   * Returns whether the skeleton should currently be shown.
   */
  isVisible(): boolean {
    return this.visible;
  }

  /**
   * Updates pending state and schedules show/hide using delay and min-hold rules.
   *
   * @param pending - True while the async load has not settled
   * @param options - When clearing pending, bypassMinHold ends the skeleton immediately
   */
  setPending(pending: boolean, options?: DelayedLoadingSettleOptions): void {
    if (this.destroyed) {
      return;
    }

    if (pending) {
      this.markPending();
      return;
    }

    this.clearPending(options);
  }

  /**
   * Marks the load as pending and schedules the delayed skeleton show when needed.
   */
  private markPending(): void {
    if (this.pending) {
      return;
    }

    this.pending = true;
    this.clearHideTimer();
    this.scheduleShowIfNeeded();
  }

  /**
   * Starts or immediately applies the show-after delay when the skeleton is not yet visible.
   */
  private scheduleShowIfNeeded(): void {
    if (this.visible || this.showTimer !== null) {
      return;
    }

    if (this.delayMs <= 0) {
      this.show();
      return;
    }

    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      if (this.destroyed || !this.pending) {
        return;
      }
      this.show();
    }, this.delayMs);
  }

  /**
   * Clears pending state and hides the skeleton using min-hold or immediate bypass rules.
   */
  private clearPending(options?: DelayedLoadingSettleOptions): void {
    if (!this.pending && !this.visible) {
      return;
    }

    this.pending = false;
    this.clearShowTimer();

    if (!this.visible) {
      return;
    }

    if (options?.bypassMinHold) {
      this.clearHideTimer();
      this.hide();
      return;
    }

    this.scheduleHideAfterMinHold();
  }

  /**
   * Hides immediately or after the remaining minimum visible hold once pending cleared.
   */
  private scheduleHideAfterMinHold(): void {
    const elapsed = this.now() - (this.shownAtMs ?? this.now());
    const remaining = Math.max(0, this.minVisibleMs - elapsed);
    if (remaining === 0) {
      this.hide();
      return;
    }

    if (this.hideTimer !== null) {
      return;
    }

    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.hide();
    }, remaining);
  }

  /**
   * Ends loading immediately (failure/timeout). Cancels show delay and skips min-hold.
   */
  fail(): void {
    if (this.destroyed) {
      return;
    }
    this.setPending(false, { bypassMinHold: true });
  }

  /**
   * Cancels timers and stops further listener emissions.
   */
  destroy(): void {
    this.destroyed = true;
    this.clearShowTimer();
    this.clearHideTimer();
  }

  /**
   * Marks the skeleton visible and notifies the listener.
   */
  private show(): void {
    this.visible = true;
    this.shownAtMs = this.now();
    this.listener(true);
  }

  private hide(): void {
    if (!this.visible) {
      return;
    }
    this.visible = false;
    this.shownAtMs = null;
    this.listener(false);
  }

  private clearShowTimer(): void {
    if (this.showTimer !== null) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }

  private clearHideTimer(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }
}
